// src/pages/GestionaSolicitudes.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useNotification } from '../context/NotificationContext';
import PlatformBackButton from '../components/PlatformBackButton';
import './GestionaSolicitudes.css';

const GestionaSolicitudes = () => {
    const [solicitudes, setSolicitudes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showHistorial, setShowHistorial] = useState(false);
    const { showNotification } = useNotification();

    useEffect(() => {
        const fetchSolicitudes = async () => {
            const q = query(collection(db, 'solicitudes_registro'), orderBy('fecha', 'desc'));
            const querySnapshot = await getDocs(q);
            const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSolicitudes(list);
            setLoading(false);
        };
        fetchSolicitudes();
    }, []);

    const resumen = useMemo(() => {
        const base = { total: solicitudes.length, pendiente: 0, contactado: 0, rechazado: 0, aprobado: 0 };
        solicitudes.forEach((sol) => {
            const estado = (sol.estado || '').toLowerCase();
            if (Object.prototype.hasOwnProperty.call(base, estado)) {
                base[estado] += 1;
            }
        });
        return base;
    }, [solicitudes]);

    const pendientes = useMemo(
        () => solicitudes.filter((sol) => (sol.estado || '').toLowerCase() === 'pendiente'),
        [solicitudes]
    );

    const historial = useMemo(
        () => solicitudes.filter((sol) => (sol.estado || '').toLowerCase() !== 'pendiente'),
        [solicitudes]
    );

    const getEstadoLabel = (estado = '') => {
        const mapping = {
            pendiente: 'Pendiente',
            contactado: 'Contactado',
            aprobado: 'Aprobado',
            rechazado: 'Rechazado',
        };
        return mapping[estado.toLowerCase()] || 'Sin estado';
    };

    const handleUpdateStatus = useCallback(async (id, nuevoEstado) => {
        try {
            const solicitudRef = doc(db, 'solicitudes_registro', id);
            await updateDoc(solicitudRef, { estado: nuevoEstado, actualizado: serverTimestamp() });
            setSolicitudes((prev) => prev.map((s) => (s.id === id ? { ...s, estado: nuevoEstado } : s)));
            showNotification(`Solicitud marcada como ${getEstadoLabel(nuevoEstado)}.`, 'success');
        } catch (error) {
            console.error('GestionaSolicitudes: error actualizando estado', error);
            showNotification('No se pudo actualizar la solicitud. Verifica tus permisos.', 'error');
        }
    }, [showNotification]);

    useEffect(() => {
        staticUpdateHandler = handleUpdateStatus;
        return () => {
            if (staticUpdateHandler === handleUpdateStatus) {
                staticUpdateHandler = null;
            }
        };
    }, [handleUpdateStatus]);

    if (loading) {
        return (
            <div className="solicitudes-shell">
                <div className="solicitudes-surface">
                    <div className="solicitudes-loading">Cargando solicitudes…</div>
                </div>
            </div>
        );
    }

    return (
        <div className="solicitudes-shell">
            <div className="solicitudes-surface">
                <div className="solicitudes-topbar">
                    <PlatformBackButton className="solicitudes-back" />
                </div>

                <div className="solicitudes-hero">
                    <span className="solicitudes-kicker">Solicitudes de demo</span>
                    <h1>Gestiona nuevos administradores</h1>
                    <p>Visualiza las peticiones recibidas desde el portal público y marca su estado conforme avances en el proceso de contacto.</p>
                </div>

                <div className="solicitudes-metrics">
                    <div className="metric-card">
                        <span className="metric-label">Pendientes</span>
                        <strong>{resumen.pendiente}</strong>
                    </div>
                    <div className="metric-card">
                        <span className="metric-label">Contactadas</span>
                        <strong>{resumen.contactado}</strong>
                    </div>
                    <div className="metric-card">
                        <span className="metric-label">Rechazadas</span>
                        <strong>{resumen.rechazado}</strong>
                    </div>
                    <div className="metric-card">
                        <span className="metric-label">Total</span>
                        <strong>{resumen.total}</strong>
                    </div>
                </div>

                <div className="solicitudes-section">
                    <header className="section-head">
                        <div>
                            <h2>Solicitudes pendientes</h2>
                            <p>Prioriza las peticiones que aún no han sido atendidas.</p>
                        </div>
                        <span className="section-count">{pendientes.length}</span>
                    </header>

                    {pendientes.length === 0 ? (
                        <div className="solicitudes-empty">
                            <h3>No hay solicitudes pendientes</h3>
                            <p>Cuando se registre una nueva petición de demo la verás en este listado.</p>
                        </div>
                    ) : (
                        pendientes.map((sol) => renderSolicitudCard(sol, false))
                    )}
                </div>

                <div className="solicitudes-section historial">
                    <header className="section-head">
                        <div>
                            <h2>Historial</h2>
                            <p>Consulta las solicitudes que ya fueron atendidas.</p>
                        </div>
                        <div className="section-actions">
                            <span className="section-count">{historial.length}</span>
                            <button
                                type="button"
                                className="historial-toggle"
                                onClick={() => setShowHistorial((prev) => !prev)}
                            >
                                {showHistorial ? 'Ocultar historial' : 'Mostrar historial'}
                            </button>
                        </div>
                    </header>

                    {showHistorial && (
                        historial.length === 0 ? (
                            <div className="solicitudes-empty">
                                <h3>No tenemos registros pasados</h3>
                                <p>Aquí aparecerán las solicitudes contactadas, aprobadas o rechazadas.</p>
                            </div>
                        ) : (
                            historial.map((sol) => renderSolicitudCard(sol, true))
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

const renderSolicitudCard = (sol, isHistorial) => {
    const estado = (sol.estado || '').toLowerCase();
    return (
        <article key={sol.id} className={`solicitud-card status-${estado}`}>
            <header className="solicitud-head">
                <div>
                    <h3 title={sol.nombreFracc}>{sol.nombreFracc}</h3>
                    <span className={`solicitud-pill pill-${estado}`}>{getEstadoLabelStatic(estado)}</span>
                </div>
                <small className="solicitud-meta">
                    Recibido: {sol.fecha?.toDate().toLocaleString() || 'Sin fecha'}
                </small>
            </header>

            <div className="solicitud-body">
                <div className="solicitud-row">
                    <span className="solicitud-tag">Contacto</span>
                    <p>{sol.nombreAdmin}</p>
                </div>
                <div className="solicitud-row">
                    <span className="solicitud-tag">Correo</span>
                    <p>{sol.email}</p>
                </div>
                <div className="solicitud-row">
                    <span className="solicitud-tag">Teléfono</span>
                    <p>{sol.celular}</p>
                </div>
                <div className="solicitud-row">
                    <span className="solicitud-tag">Lotes</span>
                    <p>{sol.numLotes}</p>
                </div>
                {sol.comentarios && (
                    <div className="solicitud-row comentarios">
                        <span className="solicitud-tag">Comentarios</span>
                        <p>{sol.comentarios}</p>
                    </div>
                )}
                {sol.emailEnviado === false && sol.emailError && (
                    <div className="solicitud-alert">
                        <span>El correo no pudo enviarse: {sol.emailError}</span>
                    </div>
                )}
            </div>

            {isHistorial ? (
                <footer className="solicitud-historial">
                    <span>
                        Última actualización:{' '}
                        {sol.actualizado?.toDate().toLocaleString() || 'Sin registro'}
                    </span>
                    {estado !== 'aprobado' && (
                        <Link to="/registrar-fraccionamiento" state={{ initialData: sol }} className="historial-retry">
                            Reintentar registro
                        </Link>
                    )}
                </footer>
            ) : (
                <footer className="solicitud-actions">
                    <Link to="/registrar-fraccionamiento" state={{ initialData: sol }} className="action-button primary">
                        Aprobar y registrar
                    </Link>
                    <div className="action-stack">
                        <button type="button" onClick={() => handleUpdateStatic(sol.id, 'contactado')} className="action-button neutral">
                            Marcar como contactado
                        </button>
                        <button type="button" onClick={() => handleUpdateStatic(sol.id, 'rechazado')} className="action-button danger">
                            Rechazar solicitud
                        </button>
                    </div>
                </footer>
            )}
        </article>
    );
};

// Helpers desacoplados para usar dentro de renderSolicitudCard sin redeclarar funciones en cada render.
const getEstadoLabelStatic = (estado = '') => {
    const mapping = {
        pendiente: 'Pendiente',
        contactado: 'Contactado',
        aprobado: 'Aprobado',
        rechazado: 'Rechazado',
    };
    return mapping[estado] || 'Sin estado';
};

let staticUpdateHandler = null;
const handleUpdateStatic = async (id, estado) => {
    if (typeof staticUpdateHandler === 'function') {
        await staticUpdateHandler(id, estado);
    }
};

export default GestionaSolicitudes;